import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, Clock, Users, Folder } from 'lucide-react';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Reports = ({ user }) => {
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalUsers: 0,
    totalHours: 0
  });
  const [timeLogs, setTimeLogs] = useState([]);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [projectsRes, usersRes, timeLogsRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/users`),
        axios.get(`${API}/timelog`)
      ]);

      const projects = Array.isArray(projectsRes.data) ? projectsRes.data : [];
      const users = Array.isArray(usersRes.data) ? usersRes.data : [];
      const timeLogs = Array.isArray(timeLogsRes.data) ? timeLogsRes.data : [];
      
      const totalHours = timeLogs.reduce((sum, log) => sum + (log.hours || 0), 0);

      setStats({
        totalProjects: projects.length,
        activeProjects: projects.filter(p => p.status === 'active').length,
        completedProjects: projects.filter(p => p.status === 'completed').length,
        totalUsers: users.length,
        totalHours: Math.round(totalHours)
      });

      setTimeLogs(timeLogs);
    } catch (error) {
      console.error('Failed to fetch reports');
      setTimeLogs([]);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, suffix = '' }) => (
    <div className="glass-card p-6 slide-up">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm mb-1">{label}</p>
          <p className="text-3xl font-bold text-white">{value}{suffix}</p>
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-container" data-testid="reports-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Reports & Analytics</h1>
        <p className="text-slate-400">Overview of your construction projects and team performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Folder}
          label="Total Projects"
          value={stats.totalProjects}
          color="bg-orange-500/20 text-orange-400"
        />
        <StatCard
          icon={BarChart3}
          label="Active Projects"
          value={stats.activeProjects}
          color="bg-blue-500/20 text-blue-400"
        />
        <StatCard
          icon={Users}
          label="Team Members"
          value={stats.totalUsers}
          color="bg-purple-500/20 text-purple-400"
        />
        <StatCard
          icon={Clock}
          label="Total Hours"
          value={stats.totalHours}
          color="bg-green-500/20 text-green-400"
          suffix=" hrs"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="glass-card p-6">
          <h2 className="text-2xl font-bold text-white mb-4">Project Status Distribution</h2>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300">Active</span>
                <span className="text-white font-bold">{stats.activeProjects}</span>
              </div>
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                  style={{ width: `${stats.totalProjects > 0 ? (stats.activeProjects / stats.totalProjects) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300">Completed</span>
                <span className="text-white font-bold">{stats.completedProjects}</span>
              </div>
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-green-600"
                  style={{ width: `${stats.totalProjects > 0 ? (stats.completedProjects / stats.totalProjects) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300">Pending</span>
                <span className="text-white font-bold">{stats.totalProjects - stats.activeProjects - stats.completedProjects}</span>
              </div>
              <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-orange-600"
                  style={{ width: `${stats.totalProjects > 0 ? ((stats.totalProjects - stats.activeProjects - stats.completedProjects) / stats.totalProjects) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card p-6">
          <h2 className="text-2xl font-bold text-white mb-4">Recent Time Logs</h2>
          {timeLogs.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No time logs available</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {timeLogs.slice(0, 5).map((log) => (
                <div key={log.id} className="p-3 bg-slate-800/50 rounded-lg" data-testid={`timelog-${log.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">
                      {new Date(log.clock_in).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                    </span>
                    {log.hours && (
                      <span className="text-orange-400 font-bold text-sm">{log.hours} hrs</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;